import { Genres } from 'enums';
import { t } from 'i18next';

interface Field {
  valueKey: string;
  nameKey: string;
  instruction?: string;
  placeholder?: string;
  rows?: number;
}

interface Offer {
  features?: string;
  benefits?: string;
  impact?: string;
  socialProof?: string;
  requirements?: string;
  tasks?: string;
}

interface Content {
  title?: string;
  companyName?: string;
  productName?: string;
  productInfo?: string;
  tableOfContents?: string;
  eventTitle?: string;
  eventDescription?: string;
  date?: string;
}

export interface TextGenerationCredentials {
  offer?: Offer;
  content?: Content;
  problems?: string;
}

const fieldsConfig: { [key: string]: Field } = {
  // own fields in request
  problems: {
    valueKey: 'problems',
    nameKey: 'problems',
    instruction: t('leadBoosterProblemsInstruction'),
    placeholder: t('leadBoosterProblemsPlaceholder'),
    rows: 3,
  },

  // fields in the offer object in request
  features: {
    valueKey: 'features',
    nameKey: 'features',
    instruction: t('leadBoosterFeaturesInstruction'),
    placeholder: t('leadBoosterFeaturesPlaceholder'),
    rows: 3,
  },
  benefits: {
    valueKey: 'benefits',
    nameKey: 'benefits',
    instruction: t('leadBoosterBenefitsInstruction'),
    placeholder: t('leadBoosterBenefitsPlaceholder'),
    rows: 3,
  },
  impact: {
    valueKey: 'impact',
    nameKey: 'impact',
    instruction: t('leadBoosterImpactInstructions'),
    placeholder: t('leadBoosterImpactPlaceholder'),
    rows: 3,
  },
  socialProof: {
    valueKey: 'offer.socialProof',
    nameKey: 'offer.socialProof',
    instruction: t('leadBoosterSocialProofInstructions'),
    placeholder: t('leadBoosterSocialProofPlaceholder'),
    rows: 3,
  },

  // fields specific for job offer
  jobTitle: {
    valueKey: 'jobTitle',
    nameKey: 'jobTitle',
    instruction: t('jobOfferJobTitleInstruction'),
    placeholder: t('jobOfferJobTitlePlaceholder'),
    rows: 1,
  },
  tasks: {
    valueKey: 'offer.tasks',
    nameKey: 'offer.tasks',
    instruction: t('jobOfferResponsibilitiesInstruction'),
    placeholder: t('jobOfferResponsibilitiesPlaceholder'),
    rows: 3,
  },
  requirements: {
    valueKey: 'offer.requirements',
    nameKey: 'offer.requirements',
    instruction: t('jobOfferRequirementsInstruction'),
    placeholder: t('jobOfferRequirementsPlaceholder'),
    rows: 3,
  },
  companyDescription: {
    valueKey: 'offer.companyDescription',
    nameKey: 'offer.companyDescription',
    instruction: t('jobOfferCompanyDescriptionInstruction'),
    placeholder: t('jobOfferCompanyDescriptionPlaceholder'),
    rows: 3,
  },
  divisionDescription: {
    valueKey: 'offer.divisionDescription',
    nameKey: 'offer.divisionDescription',
    instruction: t('jobOfferDivisionDescriptionInstruction'),
    placeholder: t('jobOfferDivisionDescriptionPlaceholder'),
    rows: 3,
  },
  benefitsJobOffer: {
    valueKey: 'offer.benefits',
    nameKey: 'offer.benefits',
    instruction: t('jobOfferBenefitsInstruction'),
    placeholder: t('jobOfferBenefitsPlaceholder'),
    rows: 3,
  },
  cta: {
    valueKey: 'offer.cta',
    nameKey: 'offer.cta',
    instruction: t('jobOfferCtaInstruction'),
    placeholder: t('jobOfferCtaPlaceholder'),
    rows: 1,
  },
  whyYou: {
    valueKey: 'offer.whyYou',
    nameKey: 'offer.whyYou',
    instruction: t('jobOfferWhyYouInstruction'),
    placeholder: t('jobOfferWhyYouPlaceholder'),
    rows: 3,
  },

  // fields in the content object in request
  eventTitle: {
    valueKey: 'content.eventTitle',
    nameKey: 'content.eventTitle',
    instruction: t('leadBoosterEventTitleInstructions'),
    placeholder: t('leadBoosterEventTitlePlaceholder'),
    rows: 3,
  },
  eventDescription: {
    valueKey: 'content.eventDescription',
    nameKey: 'content.eventDescription',
    instruction: t('leadBoosterEventDescriptionInstructions'),
    placeholder: t('leadBoosterEventDescriptionPlaceholder'),
    rows: 3,
  },
  date: {
    valueKey: 'content.date',
    nameKey: 'content.date',
    instruction: t('leadBoosterDateInstructions'),
    placeholder: t('leadBoosterDatePlaceholder'),
    rows: 3,
  },
  productName: {
    valueKey: 'content.productName',
    nameKey: 'content.productName',
    instruction: t('leadBoosterProductNameInstructions'),
    placeholder: t('leadBoosterProductNamePlaceholder'),
    rows: 3,
  },
  productInfo: {
    valueKey: 'content.productInfo',
    nameKey: 'content.productInfo',
    instruction: t('leadBoosterProductInfoInstructions'),
    placeholder: t('leadBoosterProductInfoPlaceholder'),
    rows: 3,
  },
  title: {
    valueKey: 'content.title',
    nameKey: 'content.title',
    instruction: t('leadBoosterTitleInstructions'),
    placeholder: t('leadBoosterTitlePlaceholder'),
    rows: 3,
  },
  companyName: {
    valueKey: 'content.companyName',
    nameKey: 'content.companyName',
    instruction: t('leadBoosterCompanyNameInstructions'),
    placeholder: t('leadBoosterCompanyNamePlaceholder'),
    rows: 3,
  },
  tableOfContents: {
    valueKey: 'content.tableOfContents',
    nameKey: 'content.tableOfContents',
    instruction: t('tableOfContents'),
    rows: 3,
  },
};

const genresConfig: { genres: Genres[]; requiredFields: Field[]; optionalFields: Field[] }[] = [
  {
    genres: [Genres.MARKETING_NEWSLETTER, Genres.SOCIAL_POST, Genres.SOCIAL_AD, Genres.SALES_EMAIL, Genres.CONNECTION_REQUEST],
    requiredFields: [fieldsConfig.problems, fieldsConfig.features, fieldsConfig.benefits, fieldsConfig.impact],
    optionalFields: [],
  },
  {
    genres: [Genres.BLOG_POST_TOC],
    requiredFields: [fieldsConfig.title],
    optionalFields: [fieldsConfig.companyName, fieldsConfig.productInfo],
  },
  {
    genres: [Genres.BLOG_POST],
    requiredFields: [fieldsConfig.title, fieldsConfig.tableOfContents],
    optionalFields: [fieldsConfig.companyName, fieldsConfig.productInfo],
  },
  {
    genres: [Genres.LANDING_PAGE_TOC],
    requiredFields: [],
    optionalFields: [fieldsConfig.companyName, fieldsConfig.socialProof, fieldsConfig.features, fieldsConfig.benefits, fieldsConfig.impact],
  },
  {
    genres: [Genres.LANDING_PAGE],
    requiredFields: [fieldsConfig.tableOfContents],
    optionalFields: [fieldsConfig.companyName, fieldsConfig.socialProof, fieldsConfig.features, fieldsConfig.benefits, fieldsConfig.impact],
  },
  {
    genres: [Genres.PRODUCT_DESCRIPTION],
    requiredFields: [fieldsConfig.productName, fieldsConfig.productInfo],
    optionalFields: [fieldsConfig.socialProof, fieldsConfig.features, fieldsConfig.benefits, fieldsConfig.impact],
  },
  {
    genres: [Genres.INVITATION],
    requiredFields: [fieldsConfig.eventTitle, fieldsConfig.eventDescription, fieldsConfig.date],
    optionalFields: [],
  },
  {
    genres: [Genres.JOB_OFFER],
    requiredFields: [
      fieldsConfig.jobTitle,
      fieldsConfig.companyDescription,
      fieldsConfig.divisionDescription,
      fieldsConfig.tasks,
      fieldsConfig.requirements,
      fieldsConfig.benefitsJobOffer,
      fieldsConfig.cta,
    ],
    optionalFields: [],
  },
  {
    genres: [Genres.ACTIVE_SOURCING],
    requiredFields: [
      fieldsConfig.companyDescription,
      fieldsConfig.tasks,
      fieldsConfig.requirements,
      fieldsConfig.whyYou,
    ],
    optionalFields: [],
  },
];

export default genresConfig;
